import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Save, Power, Copy, ExternalLink, Monitor, Building2 } from "lucide-react";

/**
 * Kiosk Settings — admin-only page to configure the Self-Service Kiosk.
 * Read/write hits /api/kiosk/config (admin+super_admin). Read-only preview
 * of /kiosk (the customer-facing screen) is one click away.
 */
export default function KioskSettings() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/kiosk/config").then((r) => setCfg(r.data)).catch(() => toast.error("Failed to load kiosk config"));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/kiosk/config", cfg);
      setCfg(data);
      toast.success("Kiosk settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!cfg) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  const kioskUrl = `${window.location.origin}/kiosk`;
  const togglePayment = (m) => {
    const next = cfg.allowed_payment_methods.includes(m)
      ? cfg.allowed_payment_methods.filter((x) => x !== m)
      : [...cfg.allowed_payment_methods, m];
    setCfg({ ...cfg, allowed_payment_methods: next });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Self-Service Kiosk</h1>
          <p className="text-sm text-slate-500">Configure the customer-facing OPD registration kiosk.</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={cfg.enabled ? "bg-emerald-600" : "bg-slate-400"}>
            {cfg.enabled ? "ONLINE" : "DISABLED"}
          </Badge>
          <Button variant="outline" onClick={() => window.open("/kiosk", "_blank")} className="rounded-sm" data-testid="kiosk-open"><ExternalLink className="w-4 h-4 mr-1"/> Open Kiosk</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left · Toggle + URL */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="label-tiny mb-3 flex items-center gap-1"><Power className="w-3.5 h-3.5"/> Kiosk Mode</div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" data-testid="ks-enabled" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="w-5 h-5 accent-blue-700"/>
              <div>
                <div className="text-sm font-medium text-slate-900">Enable Self-Service Kiosk</div>
                <div className="text-[11px] text-slate-500">When off, /kiosk shows "Kiosk Unavailable" and all public endpoints return 503.</div>
              </div>
            </label>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="label-tiny mb-2 flex items-center gap-1"><Monitor className="w-3.5 h-3.5"/> Kiosk URL</div>
            <div className="flex items-center gap-2 text-xs bg-slate-50 border rounded-sm px-2 py-1.5 font-mono break-all">{kioskUrl}</div>
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(kioskUrl); toast.success("Copied"); }} className="w-full mt-2 rounded-sm" data-testid="ks-copy"><Copy className="w-3.5 h-3.5 mr-1"/> Copy URL</Button>
            <div className="text-[11px] text-slate-500 mt-2">Load this URL in fullscreen (F11) on the kiosk device. No login required.</div>
          </div>
        </div>

        {/* Right · Config */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-4 space-y-4">
          <div className="text-[11px] bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-slate-600">
            <b>Note:</b> Hospital name, address, phones and logo are managed globally on the <b>Branding</b> page (super-admin only). Everything below controls kiosk behaviour.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-tiny">Kiosk ID</Label>
              <Input value={cfg.kiosk_id} onChange={(e) => setCfg({ ...cfg, kiosk_id: e.target.value })} data-testid="ks-id" className="mt-1 h-9"/>
            </div>
            <div>
              <Label className="label-tiny">Default Language</Label>
              <div className="flex gap-2 mt-1">
                {[{ k: "en-IN", l: "English" }, { k: "gu-IN", l: "ગુજરાતી" }].map((o) => (
                  <button key={o.k} data-testid={`ks-lang-${o.k}`} type="button" onClick={() => setCfg({ ...cfg, default_language: o.k })}
                    className={`px-3 py-1.5 rounded-sm text-sm border ${cfg.default_language === o.k ? "bg-blue-700 text-white border-blue-800" : "bg-white border-slate-300"}`}>{o.l}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="label-tiny">Voice Prompts</Label>
              <label className="flex items-center gap-2 mt-1 cursor-pointer">
                <input type="checkbox" data-testid="ks-voice" checked={cfg.voice_enabled} onChange={(e) => setCfg({ ...cfg, voice_enabled: e.target.checked })} className="w-4 h-4 accent-blue-700"/>
                <span className="text-sm">Enable microphone</span>
              </label>
            </div>
            <div>
              <Label className="label-tiny">Session Timeout (seconds)</Label>
              <Input type="number" min={30} value={cfg.session_timeout_seconds} onChange={(e) => setCfg({ ...cfg, session_timeout_seconds: parseInt(e.target.value, 10) || 120 })} data-testid="ks-timeout" className="mt-1 h-9"/>
            </div>
            <div>
              <Label className="label-tiny">Default Department (optional)</Label>
              <Input value={cfg.default_department} onChange={(e) => setCfg({ ...cfg, default_department: e.target.value })} data-testid="ks-dept" className="mt-1 h-9" placeholder="e.g. General OPD"/>
            </div>
            <div>
              <Label className="label-tiny">Receipt Printer</Label>
              <div className="flex gap-2 mt-1">
                {[{ k: "thermal58", l: "58 mm" }, { k: "thermal80", l: "80 mm" }, { k: "a4", l: "A4" }].map((o) => (
                  <button key={o.k} data-testid={`ks-printer-${o.k}`} type="button" onClick={() => setCfg({ ...cfg, printer: o.k })}
                    className={`px-3 py-1.5 rounded-sm text-sm border ${cfg.printer === o.k ? "bg-blue-700 text-white border-blue-800" : "bg-white border-slate-300"}`}>{o.l}</button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <Label className="label-tiny">Allowed Payment Methods</Label>
            <div className="flex gap-2 mt-1 flex-wrap">
              {["Cash", "UPI", "Credit"].map((m) => {
                const on = cfg.allowed_payment_methods.includes(m);
                return (
                  <button key={m} type="button" data-testid={`ks-pay-${m}`} onClick={() => togglePayment(m)}
                    className={`px-3 py-1.5 rounded-sm text-sm border ${on ? "bg-blue-700 text-white border-blue-800" : "bg-white border-slate-300"}`}>{m}</button>
                );
              })}
            </div>
            <div className="text-[11px] text-slate-500 mt-2">Credit is only shown when explicitly enabled. Removing all modes will block registrations.</div>
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={busy} className="bg-blue-700 hover:bg-blue-800 rounded-sm" data-testid="ks-save"><Save className="w-4 h-4 mr-1"/> Save Settings</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
